import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2, PlayCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { patientsApi } from '@/api/endpoints/patients.api';
import type { PatientMedication } from '@/types/patient.types';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const reactivateSchema = z.object({
  reason: z.string().max(500, 'Motivo deve ter no máximo 500 caracteres').optional(),
});

type ReactivateFormData = z.infer<typeof reactivateSchema>;

interface ReactivateMedicationDialogProps {
  patientId: string;
  medication: PatientMedication;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: (medication: PatientMedication) => void;
}

export function ReactivateMedicationDialog({
  patientId,
  medication,
  open,
  onOpenChange,
  onSuccess,
}: ReactivateMedicationDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ReactivateFormData>({
    resolver: zodResolver(reactivateSchema),
    defaultValues: {
      reason: '',
    },
  });

  const onSubmit = async (data: ReactivateFormData) => {
    setIsSubmitting(true);
    setError(null);

    try {
      const updatedMedication = await patientsApi.reactivateMedication(patientId, medication.id, {
        reason: data.reason || undefined,
      });
      onSuccess(updatedMedication);
      reset();
      onOpenChange(false);
    } catch (err: unknown) {
      console.error('Erro ao reativar medicamento:', err);

      // Verifica se é erro de permissão (403 Forbidden)
      const axiosError = err as { response?: { status?: number } };
      if (axiosError.response?.status === 403) {
        setError('Você não tem permissão para reativar medicamentos. Esta ação é restrita a médicos.');
      } else {
        setError('Erro ao reativar medicamento. Tente novamente.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    reset();
    setError(null);
    onOpenChange(false);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch {
      return dateString;
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PlayCircle className="h-5 w-5 text-green-500" />
            Reativar Medicamento
          </DialogTitle>
          <DialogDescription>
            Você está prestes a reativar o medicamento <strong>{medication.medicationName}</strong>.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Informações da suspensão */}
          <div className="rounded-md bg-muted p-3 space-y-2 text-sm">
            <div>
              <span className="font-medium">Suspenso em:</span>{' '}
              {formatDate(medication.suspendedAt)}
            </div>
            {medication.suspendedByName && (
              <div>
                <span className="font-medium">Suspenso por:</span>{' '}
                {medication.suspendedByName}
              </div>
            )}
            {medication.suspendedReason && (
              <div>
                <span className="font-medium">Motivo da suspensão:</span>{' '}
                {medication.suspendedReason}
              </div>
            )}
          </div>

          <div className="rounded-md bg-green-50 border border-green-200 p-3 text-sm text-green-800">
            <strong>Informação:</strong> A reativação do medicamento será registrada no histórico do paciente,
            incluindo a data e o profissional responsável.
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason">Motivo da Reativação (opcional)</Label>
            <Textarea
              id="reason"
              placeholder="Descreva o motivo da reativação do medicamento..."
              rows={3}
              {...register('reason')}
            />
            {errors.reason && (
              <p className="text-sm text-destructive">{errors.reason.message}</p>
            )}
          </div>

          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button type="submit" className="bg-green-600 hover:bg-green-700" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Reativar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
